import { format } from "date-fns";
import { Eye, EyeOff, Pencil, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { Entry } from "./types";

type Props = {
  entries: Entry[] | undefined;
  isLoading: boolean;
  togglePending: boolean;
  onTogglePublish: (entry: Entry) => void;
  onEdit: (entry: Entry) => void;
  onDelete: (id: string) => void;
};

export const ChangelogEntriesList = ({
  entries, isLoading, togglePending, onTogglePublish, onEdit, onDelete,
}: Props) => {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    );
  }
  if (!entries || entries.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No entries yet. Create the first one with "New entry".
      </p>
    );
  }
  return (
    <ul className="divide-y">
      {entries.map((entry) => (
        <li key={entry.id} className="flex items-center justify-between gap-3 py-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Badge variant={entry.published_at ? "default" : "secondary"} className="text-[10px]">
                {entry.published_at ? "Published" : "Draft"}
              </Badge>
              <Badge variant="outline" className="text-[10px] capitalize">{entry.category}</Badge>
              <span className="truncate text-sm font-medium">{entry.title}</span>
            </div>
            <p className="mt-1 truncate text-xs text-muted-foreground">
              /{entry.slug} ·{" "}
              {entry.published_at
                ? `Published ${format(new Date(entry.published_at), "PP")}`
                : `Updated ${format(new Date(entry.updated_at), "PP")}`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost" size="icon"
              title={entry.published_at ? "Unpublish" : "Publish now"}
              onClick={() => onTogglePublish(entry)}
              disabled={togglePending}
            >
              {entry.published_at ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="icon" title="Edit" onClick={() => onEdit(entry)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" title="Delete" onClick={() => onDelete(entry.id)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
};
